// An antd Input with the `{Slicer.` menu: a drop-in for `Input` on every title field that
// `resolveTemplate` renders. It brings the Input adapter, the hook, the menu, an optional
// hint line and the template warnings. The `ref` handle inserts a complete token over the
// selection, which is how the help popover writes into the field.

import { useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Input, Typography } from "antd";
import type { InputProps, InputRef } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import { useSlicerVariables } from "../../Contexts/dashboardHooks";
import { resolveTemplate } from "../../Utils/resolveTemplate";
import { isMalformedSlicerWarning } from "../../Utils/resolveTemplate.mjs";
import { spliceText } from "../../Utils/slicerTokenTrigger.mjs";
import { SlicerTokenMenu } from "./SlicerTokenMenu";
import { optionId, useSlicerTokenTrigger, type SlicerTokenAdapter } from "./useSlicerTokenTrigger";

const { Text } = Typography;

const DEFAULT_MAX_LENGTH = 200;

export interface SlicerTokenInputHandle {
  /** Insert complete token text over the current selection and keep the focus. */
  insert(text: string): void;
  focus(): void;
}

export interface SlicerTokenInputProps extends Omit<InputProps, "value" | "onChange"> {
  value: string;
  onChange: (next: string) => void;
  hint?: React.ReactNode;
  /** Show the `resolveTemplate` warnings under the field; on by default. */
  showWarnings?: boolean;
  ref?: React.Ref<SlicerTokenInputHandle>;
}

export function SlicerTokenInput({
  value,
  onChange,
  hint,
  showWarnings = true,
  maxLength = DEFAULT_MAX_LENGTH,
  ref,
  onKeyDown,
  onBlur,
  onSelect,
  ...rest
}: SlicerTokenInputProps) {
  const inputRef = useRef<InputRef>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  const maxLengthRef = useRef(maxLength);
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
    maxLengthRef.current = maxLength;
  });

  const [limitHit, setLimitHit] = useState(false);
  const [minWidth, setMinWidth] = useState<number>();
  const slicerValues = useSlicerVariables();

  // The DOM, not React state, holds the caret and the new text inside `onChange`.
  const adapter = useMemo<SlicerTokenAdapter>(
    () => ({
      caretText() {
        const input = inputRef.current?.input;
        if (!input) return null;
        const { selectionStart, selectionEnd } = input;
        if (selectionStart == null || selectionEnd == null || selectionStart !== selectionEnd) return null;
        return { before: input.value.slice(0, selectionStart), after: input.value.slice(selectionStart), base: 0 };
      },
      selection() {
        const input = inputRef.current?.input;
        if (!input) return null;
        const from = input.selectionStart ?? input.value.length;
        return { from, to: input.selectionEnd ?? from };
      },
      replace(from, to, text, caretOffset) {
        const input = inputRef.current?.input;
        if (!input) return;
        const next = spliceText(input.value, from, to, text, maxLengthRef.current);
        if (next == null) {
          setLimitHit(true);
          return;
        }
        setLimitHit(false);
        pendingCaret.current = from + (caretOffset ?? text.length);
        onChangeRef.current(next);
      },
    }),
    [],
  );

  const trigger = useSlicerTokenTrigger(adapter);
  const { menu, model, listId, refresh, close, insert } = trigger;

  // Restore the caret after a programmatic value and re-parse once the value committed.
  useLayoutEffect(() => {
    const input = inputRef.current?.input;
    const caret = pendingCaret.current;
    if (input && caret != null) {
      pendingCaret.current = null;
      input.focus();
      input.setSelectionRange(caret, caret);
    }
    refresh();
  }, [value, refresh]);

  useLayoutEffect(() => {
    if (menu.open) setMinWidth(anchorRef.current?.getBoundingClientRect().width);
  }, [menu.open]);

  useImperativeHandle(
    ref,
    () => ({
      insert: (text: string) => {
        insert(text);
      },
      focus: () => inputRef.current?.focus(),
    }),
    [insert],
  );

  const warnings = useMemo(
    () => (showWarnings ? resolveTemplate(value, slicerValues).warnings : []),
    [value, slicerValues, showWarnings],
  );
  const limitWarning = limitHit ? `The title is limited to ${maxLength} characters, so the token was not inserted.` : null;
  // The coach is in the middle of the token while the menu is open, so the brace-scan warning waits.
  const visible = menu.open ? warnings.filter((w) => !isMalformedSlicerWarning(w)) : warnings;
  const shown = limitWarning ? [...visible, limitWarning] : visible;
  const activeIndex = menu.open ? menu.activeIndex : 0;

  return (
    <div className="slicer-token-field">
      <SlicerTokenMenu
        open={menu.open}
        model={model}
        activeIndex={activeIndex}
        listId={listId}
        onPick={(index) => trigger.pick(index)}
        onHover={trigger.setActive}
        minWidth={minWidth}
      >
        <div ref={anchorRef} className="slicer-token-anchor">
          <Input
            {...rest}
            ref={inputRef}
            value={value}
            maxLength={maxLength}
            onChange={(e) => {
              onChange(e.target.value);
              refresh();
            }}
            onSelect={(e) => {
              onSelect?.(e);
              refresh();
            }}
            onBlur={(e) => {
              onBlur?.(e);
              close();
            }}
            onKeyDown={(e) => {
              if (trigger.onKeyDown(e.nativeEvent)) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              onKeyDown?.(e);
            }}
            aria-autocomplete="list"
            aria-expanded={menu.open}
            aria-controls={menu.open ? listId : undefined}
            aria-activedescendant={menu.open && model && model.pickable > 0 ? optionId(listId, activeIndex) : undefined}
          />
        </div>
      </SlicerTokenMenu>

      {hint && (
        <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: "block" }}>
          {hint}
        </Text>
      )}

      {shown.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          {shown.map((w, i) => (
            <Text key={i} type="warning" style={{ fontSize: 12 }}>
              <WarningOutlined style={{ marginRight: 4 }} />
              {w}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
}
